import { styled } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import {
    Box,
    CardContent,
    Card as MuiCard,
    Divider as MuiDivider,
    Tab,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { spacing } from '@mui/system'
import { useTranslation } from 'react-i18next'

import { useAtom } from 'jotai'
import { warrantActiveTabAtom, warrantGridCurrentPageAtom, warrantFiltersAtom } from '../../atoms/warrantAtom'
import { WarrantsFilter } from './WarrantsFilter'
import WarrantsGrid from '../../components/entities/WarrantsGrid'
import { FluentWidthContainer } from '../components/FluentWidthContainer'
import LoadingIndicator from '../../components/LoadingIndicator'
import { findSubscriptionPeriodCloseToDate } from '../../utils/warrants'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import { getSectorIdFromIssueOrWarrant } from '../issues/filters/utils/sectors'
import { useWatchListFilter } from '../../hooks/useWatchListFilter'
import { EnrichedWarrantEntity, WarrantEntity } from 'types/WarrantEntity'
import { addDays } from 'date-fns'
import { GridSortModel } from '@mui/x-data-grid/models'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

export const isFutureWarrant = (now: Date, warrant: EnrichedWarrantEntity) => {
    const subEndDate = warrant.currentSubscriptionPeriod?.subEndDate || warrant.subEndDate
    const subStartDate = warrant.currentSubscriptionPeriod?.subStartDate || warrant.subStartDate
    // if either of the dates are null, categorize as future
    if (!subStartDate || !subEndDate) {
        return true
    }
    const subStartDateD = new Date(subStartDate)
    const subEndDateD = new Date(subEndDate)

    if (!subStartDateD || !subEndDateD || now < subStartDateD) {
        return true
    } else return false
}

export const isPreviousWarrant = (now: Date, warrant: EnrichedWarrantEntity) => {
    const subEndDate = warrant.currentSubscriptionPeriod?.subEndDate || warrant.subEndDate

    if (!subEndDate) {
        return false
    }
    const endDate = addDays(new Date(subEndDate), 1)

    if (now > endDate) {
        return true
    } else return false
}

/**
 * Categorize warrants into previous, current and future, by looking at the current date
 * and comparing it to subStartDate and subEndDate in the warrants.
 *
 * @param {Array} warrants List of warrants to categorize
 * @returns {Object} Object with keys previousWarrants, currentWarrants and futureWarrants
 */
export function categorizeWarrants<T extends WarrantEntity>(warrants: T[]) {
    const now = new Date()
    const previousWarrants: T[] = []
    const currentWarrants: T[] = []
    const futureWarrants: T[] = []

    warrants.forEach((warrant) => {
        if (isFutureWarrant(now, warrant)) {
            futureWarrants.push(warrant)
        } else if (isPreviousWarrant(now, warrant)) {
            previousWarrants.push(warrant)
        } else {
            currentWarrants.push(warrant)
        }
    })
    return { previousWarrants, currentWarrants, futureWarrants }
}

function WarrantsTabs({ warrants }: { warrants: EnrichedWarrantEntity[] }) {
    const { t } = useTranslation()
    const [tabValue, setTabValue] = useAtom(warrantActiveTabAtom)
    const [, setWarrantGridCurrentPage] = useAtom(warrantGridCurrentPageAtom)
    const [filters, setFilters] = useAtom(warrantFiltersAtom)
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    const { previousWarrants, currentWarrants, futureWarrants } = categorizeWarrants(warrants)

    const defaultSorting: GridSortModel = [{ field: 'subEndDate', sort: 'asc' }]
    const defaultPreviousSorting: GridSortModel = [{ field: 'subEndDate', sort: 'desc' }]

    const updateTab = (newValue: typeof tabValue) => {
        if (
            tabValue !== 'previous' &&
            newValue === 'previous' &&
            (filters.inTheMoney.value.includes('only-out-of-money') ||
                filters.inTheMoney.value.includes('only-in-the-money'))
        ) {
            setFilters((prevFilters) => ({
                ...prevFilters,
                inTheMoney: { value: ['all'] },
            }))
        }
        setWarrantGridCurrentPage(0)
        setTabValue(newValue)
    }

    return (
        <Card mb={6}>
            <CardContent>
                <TabContext value={tabValue}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList
                            onChange={(e, newValue) => {
                                updateTab(newValue)
                            }}
                        >
                            <Tab value='current' label={t('site.issues.current')} />
                            <Tab value='future' label={t('site.issues.future')} />
                            <Tab value='previous' label={t('site.issues.previous')} />
                        </TabList>
                    </Box>
                    <TabPanel value='previous' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                        <WarrantsGrid
                            warrants={previousWarrants}
                            fields={[
                                'name',
                                'subEndDate',
                                'warrantSubPrice',
                                ...(!isSmallScreen ? ['closePrice', 'subscribedAmount', 'unlisted', 'outcome'] : []),
                            ]}
                            sortModel={defaultPreviousSorting}
                        />
                    </TabPanel>
                    <TabPanel value='current' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                        <WarrantsGrid
                            warrants={currentWarrants}
                            fields={[
                                'name',
                                'subEndDate',
                                'warrantSubPrice',
                                ...(!isSmallScreen
                                    ? ['closePrice', 'currentSharePrice', 'warrantAmount', 'unlisted']
                                    : []),
                            ]}
                            sortModel={defaultSorting}
                        />
                    </TabPanel>
                    <TabPanel value='future' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                        <WarrantsGrid
                            warrants={futureWarrants}
                            fields={[
                                'name',
                                'subEndDate',
                                'warrantSubPrice',
                                ...(!isSmallScreen
                                    ? ['closePrice', 'currentSharePrice', 'warrantAmount', 'unlisted']
                                    : []),
                            ]}
                            sortModel={defaultSorting}
                        />
                    </TabPanel>
                    {/* <TabPanel value='all'>
                        <WarrantsGrid warrants={warrants} />
                    </TabPanel> */}
                </TabContext>
            </CardContent>
        </Card>
    )
}

function WarrantsView() {
    const { t } = useTranslation()
    const [warrants, setWarrants] = useState([])
    const [filteredWarrants, setFilteredWarrants] = useState([])
    const [filters] = useAtom(warrantFiltersAtom)
    const { result: fetchedWarrants } = useFetchEntitiesWithCache('warrant')
    const { watchListFilter } = useWatchListFilter(filters.onlyInWatchLists.value)
    useLogUserEvent('warrant')

    useEffect(() => {
        if (!fetchedWarrants) return

        fetchedWarrants.forEach((warrant) => {
            const now = new Date().toISOString()
            const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, now)
            if (currentPeriod) {
                warrant.currentSubscriptionPeriod = currentPeriod
                warrant.subStartDate = currentPeriod.subStartDate
                warrant.subEndDate = currentPeriod.subEndDate
            }
        })

        setWarrants(fetchedWarrants)
    }, [fetchedWarrants])

    useEffect(() => {
        if (!warrants.length) return

        const filtered = warrants.filter((warrant) => {
            const subPrice =
                warrant.currentSubscriptionPeriod?.warrantSubPrice || warrant.currentSubscriptionPeriod?.prelSubPrice
            const isVwap = warrant.currentSubscriptionPeriod?.isVwap
            const closePrice = warrant.closePrice

            const inTheMoneyFilter = (() => {
                if (filters.inTheMoney.value.includes('all')) {
                    return true
                } else if (filters.inTheMoney.value.includes('only-in-the-money')) {
                    return closePrice >= subPrice
                } else if (filters.inTheMoney.value.includes('only-out-of-money')) {
                    return closePrice < subPrice
                } else if (filters.inTheMoney.value.includes('only-floating-price')) {
                    return isVwap
                } else {
                    return !isVwap
                }
            })()

            return (
                watchListFilter(warrant) &&
                (filters.sectors.value.length === 0 ||
                    (![warrant.sectorLevel1, warrant.sectorLevel2].includes(null) &&
                        filters.sectors.value.includes(getSectorIdFromIssueOrWarrant(warrant)))) &&
                (filters.marketplaces.value.length === 0 ||
                    filters.marketplaces.value.includes(warrant.marketplaceId)) &&
                (filters.listed.value.includes('all') ||
                    (filters.listed.value.includes('only-listed') ? !warrant.unlisted : warrant.unlisted)) &&
                inTheMoneyFilter
            )
        })

        setFilteredWarrants(filtered)
    }, [watchListFilter, filters, warrants])

    return (
        <React.Fragment>
            <Helmet title={t('entities.warrant.plural')} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                }}
            >
                {t('entities.warrant.plural')}
            </Typography>
            <Divider my={6} />
            <FluentWidthContainer>
                <LoadingIndicator
                    loading={!warrants}
                    label={t('site.loading.warrants')}
                    labelSx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                />
                {warrants.length ? <WarrantsFilter warrants={warrants} /> : null}
                {warrants.length ? <WarrantsTabs warrants={filteredWarrants} /> : null}
            </FluentWidthContainer>
        </React.Fragment>
    )
}

export default WarrantsView
